import { Flex } from '@chakra-ui/react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { Checkbox, FormFeedback, Input, TextArea } from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';

import {
  DIGITS_ONLY_VALIDATION_KEYS,
  getNumericValidationError,
  getPairedValidationValue,
  validateOptionalTrimmedText
} from '../helpers';
import { FieldFormValues } from '../../../helpers/articleTypeConfig.types';
import { formatKeyPath } from '../../../helpers/articleTypeConfig.utils';
import AllowedFormatsMultiSelect from '../../AllowedFormatsMultiSelect';
import { isAuthorsVisibilityFieldKey } from './AuthorsVisibilitySelectField';

type ValidationFieldDefinition = {
  key: string;
  inputType: 'string' | 'number' | 'boolean' | 'array' | 'object';
};

type Props = {
  definitions: ValidationFieldDefinition[];
  control: Control<FieldFormValues>;
  errors: FieldErrors<FieldFormValues>;
  validationValues?: Record<string, unknown>;
  allowedFileFormatOptions: string[];
  currentFieldName?: string;
  isFormDisabled: boolean;
};

const ValidationFieldRenderer: React.FC<Props> = ({
  definitions,
  control,
  errors,
  validationValues,
  allowedFileFormatOptions,
  currentFieldName,
  isFormDisabled
}) => {
  const validationErrors = (errors.validationValues || {}) as Record<
    string,
    { message?: string }
  >;

  return (
    <>
      {definitions.map(definition => {
        const { key, inputType } = definition;
        const fieldLabel = formatKeyPath(key);
        const validationError = validationErrors[key];

        if (key === 'allowedFileFormats') {
          return (
            <Flex key={key} direction="column" gap={2}>
              <FieldLabel label={fieldLabel} subText="" isRequired={false} />
              <Controller
                name="allowedFileFormats"
                control={control}
                render={({ field }) => (
                  <AllowedFormatsMultiSelect
                    options={allowedFileFormatOptions}
                    selectedValues={field.value || []}
                    onChange={field.onChange}
                    isDisabled={isFormDisabled}
                    currentField={currentFieldName ?? ''}
                  />
                )}
              />
            </Flex>
          );
        }

        if (inputType === 'boolean') {
          return (
            <Controller
              key={key}
              name={`validationValues.${key}` as never}
              control={control}
              render={({ field }) => (
                <Flex direction="column" gap={2}>
                  <Checkbox
                    id={`validation-${key}`}
                    name={field.name}
                    ariaLabel={fieldLabel}
                    isChecked={Boolean(field.value)}
                    onChange={e => field.onChange(e.target.checked)}
                    disabled={isFormDisabled}
                  >
                    {fieldLabel}
                  </Checkbox>
                </Flex>
              )}
            />
          );
        }

        if (inputType === 'array' || inputType === 'object') {
          return (
            <Controller
              key={key}
              name={`validationValues.${key}` as never}
              control={control}
              rules={{
                validate: val =>
                  validateOptionalTrimmedText({
                    value: typeof val === 'string' ? val : '',
                    label: fieldLabel,
                    isDisabled: isFormDisabled
                  })
              }}
              render={({ field }) => (
                <Flex direction="column" gap={2}>
                  <FieldLabel
                    label={fieldLabel}
                    subText=""
                    isRequired={false}
                  />
                  <TextArea
                    name={field.name}
                    value={String(field.value ?? '')}
                    onChange={e => field.onChange(e.target.value)}
                    placeholder={
                      inputType === 'array'
                        ? 'Comma separated values'
                        : 'JSON object value'
                    }
                    resize="vertical"
                    isInvalid={Boolean(validationError)}
                    errorMessage={validationError?.message}
                    isReadOnly={isFormDisabled}
                  />
                </Flex>
              )}
            />
          );
        }

        if (inputType === 'string' && isAuthorsVisibilityFieldKey(key)) {
          return (
            <></>
            // Will enable this code when author form will be dynamic throughout the page
            //<AuthorsVisibilitySelectField
            //   key={key}
            //   fieldKey={key}
            //   fieldLabel={fieldLabel}
            //   control={control}
            //   validationError={validationError}
            //   isFormDisabled={isFormDisabled}
            // />
          );
        }

        return (
          <Controller
            key={key}
            name={`validationValues.${key}` as never}
            control={control}
            rules={{
              validate: val => {
                if (inputType !== 'number') {
                  return validateOptionalTrimmedText({
                    value: typeof val === 'string' ? val : '',
                    label: fieldLabel,
                    isDisabled: isFormDisabled
                  });
                }

                if (isFormDisabled) return true;

                const rawValue =
                  typeof val === 'string' ? val : String(val ?? '');

                return getNumericValidationError({
                  key,
                  value: rawValue,
                  pairedValue: getPairedValidationValue(key, validationValues)
                });
              }
            }}
            render={({ field }) => (
              <Flex direction="column" gap={2}>
                <FieldLabel label={fieldLabel} subText="" isRequired={false} />
                <Input
                  name={field.name}
                  type={
                    inputType === 'number' &&
                    DIGITS_ONLY_VALIDATION_KEYS.has(key)
                      ? 'text'
                      : inputType === 'number'
                      ? 'number'
                      : 'text'
                  }
                  value={String(field.value ?? '')}
                  onChange={e => {
                    const rawValue = e.target.value;
                    const nextValue =
                      inputType === 'number' &&
                      DIGITS_ONLY_VALIDATION_KEYS.has(key)
                        ? rawValue.replace(/[^0-9]/g, '')
                        : rawValue;

                    field.onChange(nextValue);
                  }}
                  inputMode={
                    inputType === 'number' &&
                    DIGITS_ONLY_VALIDATION_KEYS.has(key)
                      ? 'numeric'
                      : undefined
                  }
                  pattern={
                    inputType === 'number' &&
                    DIGITS_ONLY_VALIDATION_KEYS.has(key)
                      ? '[0-9]*'
                      : undefined
                  }
                  placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                  invalid={Boolean(validationError)}
                  disabled={isFormDisabled}
                />
                {validationError ? (
                  <FormFeedback variant="error">
                    {validationError.message}
                  </FormFeedback>
                ) : null}
              </Flex>
            )}
          />
        );
      })}
    </>
  );
};

export default ValidationFieldRenderer;
